import { useState } from 'react';
import { Trash2, Settings } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { useMapStore } from '@/stores/mapStore';
import { useToast } from '@/hooks/useToast';
import { saveSettings } from '@/api/settings';
import { clearContext } from '@/api/ares';
import { clearHistory } from '@/api/fleet';
import { STORAGE_KEYS } from '@/utils/constants';
import Modal from '@/components/ui/Modal';
import ThemeToggle from './ThemeToggle';

export default function SettingsModal() {
  const settingsOpen = useUIStore((s) => s.settingsOpen);
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const getSessionId = useAuthStore((s) => s.getSessionId);
  const resetSessionId = useAuthStore((s) => s.resetSessionId);
  const clearMessages = useChatStore((s) => s.clearMessages);
  const clearFeatures = useMapStore((s) => s.clearFeatures);
  const setRouteData = useMapStore((s) => s.setRouteData);
  const setLivePosition = useMapStore((s) => s.setLivePosition);
  const clearRouteLines = useMapStore((s) => s.clearRouteLines);
  const setRoutePanelOpen = useUIStore((s) => s.setRoutePanelOpen);
  const setFleetPanelOpen = useUIStore((s) => s.setFleetPanelOpen);
  const setChartModalOpen = useUIStore((s) => s.setChartModalOpen);
  const setCurrentExportId = useUIStore((s) => s.setCurrentExportId);
  const theme = useUIStore((s) => s.theme);
  const { toast } = useToast();

  const [sessionDuration, setSessionDuration] = useState(
    localStorage.getItem(STORAGE_KEYS.SESSION_DURATION) || '24h'
  );

  const handleSave = async () => {
    try {
      const sessionId = getSessionId();
      await saveSettings({
        session_id: sessionId,
        settings: {
          session_duration: sessionDuration,
          theme,
        },
      });
      localStorage.setItem(STORAGE_KEYS.SESSION_DURATION, sessionDuration);
      toast('Configuración guardada', 'success');
      setSettingsOpen(false);
    } catch {
      toast('Error al guardar configuración', 'error');
    }
  };

  const handleClearHistory = async () => {
    const sessionId = getSessionId();
    try {
      clearMessages();
      clearFeatures();
      clearRouteLines();
      setRouteData(null);
      setLivePosition(null);
      setRoutePanelOpen(false);
      setFleetPanelOpen(false, []);
      setChartModalOpen(false);
      setCurrentExportId(null);
      await clearContext(sessionId);
      await clearHistory(sessionId);
      resetSessionId();
      toast('Historial limpiado', 'success');
    } catch {
      toast('Error al limpiar historial', 'error');
    }
  };

  return (
    <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} className="max-w-[500px]">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="text-accent-blue" size={18} />
        <h2 className="text-lg font-bold text-text-primary">Configuración</h2>
      </div>

      {/* Theme */}
      <div className="mb-6 pb-6 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-accent-blue uppercase tracking-wider mb-4">
          Apariencia
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-text-primary">Tema</div>
            <div className="text-xs text-text-muted mt-1">
              Selecciona entre modo oscuro y claro
            </div>
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Session duration */}
      <div className="mb-6 pb-6 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-accent-blue uppercase tracking-wider mb-4">
          Sesión
        </h3>
        <div>
          <label className="text-sm text-text-primary mb-2 block">
            Duración de sesión
          </label>
          <select
            value={sessionDuration}
            onChange={(e) => setSessionDuration(e.target.value)}
            className="w-full px-3 py-2.5 bg-bg-primary border border-border-subtle text-text-primary rounded-lg outline-none text-sm"
          >
            <option value="24h">24 horas</option>
            <option value="2d">2 días</option>
            <option value="3d">3 días</option>
            <option value="4d">4 días</option>
            <option value="5d">5 días</option>
          </select>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-accent-red uppercase tracking-wider mb-4">
          Zona Peligrosa
        </h3>
        <button
          onClick={handleClearHistory}
          className="w-full text-left bg-accent-red/10 text-accent-red border border-accent-red/30 px-4 py-2.5 rounded-lg hover:bg-accent-red/20 hover:border-accent-red transition-all flex items-center gap-2.5 font-medium text-sm"
        >
          <Trash2 size={14} />
          Limpiar todo el historial
        </button>
        <p className="text-xs text-text-muted mt-2">
          Esto borrará todo el historial de chat y reiniciará el contexto.
        </p>
      </div>

      {/* Footer */}
      <div className="flex justify-end mt-4">
        <button
          onClick={handleSave}
          className="bg-accent-blue text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-accent-purple transition-colors text-sm"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}
